import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { Button, TextField, Callout } from "@/design/components";
import { acceptInvite } from "@/api/auth";
import { ApiError } from "@/api/client";
import authStyles from "./AuthForm.module.css";

// Mirrors api acceptInviteSchema (password min 8) + a client-only confirm field.
const acceptSchema = z
  .object({
    password: z.string().min(8, "At least 8 characters"),
    confirm: z.string().min(1, "Re-enter your password"),
  })
  .refine((v) => v.password === v.confirm, { path: ["confirm"], error: "Passwords don't match" });
type AcceptValues = z.infer<typeof acceptSchema>;

/**
 * Invited member completes onboarding: lands here from the emailed
 * `/auth/accept-invite?token=…` link and sets a password to activate the account
 * (consumes the one-time token), then signs in.
 */
export function AcceptInviteScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [formError, setFormError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AcceptValues>({
    resolver: zodResolver(acceptSchema),
    defaultValues: { password: "", confirm: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await acceptInvite(token, values.password);
      setAccepted(true);
    } catch (err) {
      setFormError(
        err instanceof ApiError
          ? err.message
          : "We couldn't accept this invite. The link may have expired or already been used.",
      );
    }
  });

  return (
    <AuthLayout>
      <header className={authStyles.head}>
        <p className="u-eyebrow">Accept invitation</p>
        <h2 className={authStyles.title}>{accepted ? "You're all set" : "Set your password"}</h2>
      </header>

      {!token && (
        <Callout tone="danger" title="Missing token">
          This invite link is incomplete. Use the most recent link from your email.
        </Callout>
      )}

      {token && accepted && (
        <>
          <Callout tone="success" title="Account activated">
            Your password is set. Sign in to reach your organization's control plane.
          </Callout>
          <Button size="lg" fullWidth onClick={() => navigate("/login", { replace: true })}>
            Continue to sign in
          </Button>
        </>
      )}

      {token && !accepted && (
        <form className={authStyles.form} onSubmit={onSubmit} noValidate>
          {formError && (
            <div className={authStyles.formError} role="alert">
              {formError}
            </div>
          )}
          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            placeholder="At least 8 characters"
            required
            error={errors.password?.message}
            {...register("password")}
          />
          <TextField
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            placeholder="Re-enter your password"
            required
            error={errors.confirm?.message}
            {...register("confirm")}
          />
          <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
            Activate account
          </Button>
        </form>
      )}

      <p className={authStyles.alt}>
        Already activated? <Link to="/login">Sign in</Link>
      </p>
    </AuthLayout>
  );
}
